import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, Observable, throwError} from 'rxjs';
import {environment} from '../../../environments/environment';

@Injectable({providedIn: 'root'})
export class BaseApiService {
  protected apiUrl = `${environment.apiUrl}/api/v1`;

  constructor(private http: HttpClient) {}

  protected get<T>(url: string, params?: Record<string, any>, additionalHeaders?: Record<string, string>): Observable<T> {
    const options: {params: Record<string, string | string[]>; headers: typeof additionalHeaders; responseType: 'json'} = {
      params: this.cleanParams(params),
      headers: additionalHeaders,
      responseType: 'json' as const
    };

    return this.http.get<T>(`${this.apiUrl}/${url}`, options).pipe(
      catchError(error => this.handleError(error))
    );
  }

  protected post<T>(url: string, body: any, additionalHeaders?: Record<string, string>): Observable<T> {
    return this.http.post<T>(`${this.apiUrl}/${url}`, body, {
      headers: additionalHeaders,
      responseType: 'json' as const
    }).pipe(
      catchError(error => this.handleError(error))
    );
  }

  protected delete<T>(url: string, additionalHeaders?: Record<string, string>): Observable<T> {
    return this.http.delete<T>(`${this.apiUrl}/${url}`, {
      headers: additionalHeaders,
      responseType: 'json' as const
    }).pipe(
      catchError(error => this.handleError(error))
    );
  }

  protected put<T>(url: string, body: any, additionalHeaders?: Record<string, string>): Observable<T> {
    return this.http.put<T>(`${this.apiUrl}/${url}`, body, {
      headers: additionalHeaders,
      responseType: 'json' as const
    }).pipe(
      catchError(error => this.handleError(error))
    );
  }

  protected patch<T>(url: string, body: any, additionalHeaders?: Record<string, string>): Observable<T> {
    return this.http.patch<T>(`${this.apiUrl}/${url}`, body, {
      headers: additionalHeaders,
      responseType: 'json' as const
    }).pipe(
      catchError(error => this.handleError(error))
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    let message: string;

    if (error.error?.message) {
      // Backend sent a custom message - use it
      message = error.error.message;
    } else if (error.status === 0) {
      // Network error, server unreachable
      message = 'Unable to connect. Please check your internet connection.';
    } else if (error.status >= 500) {
      // Server error without custom message (502, 503, etc)
      message = 'Service temporarily unavailable. Please try again.';
    } else if (error.status === 404) {
      message = 'The requested resource was not found.';
    } else if (error.status === 401 || error.status === 403) {
      message = 'You do not have permission to perform this action.';
    } else {
      message = 'Something went wrong. Please try again.';
    }

    // Keep the backend's error details so callers can act on them
    return throwError(() => Object.assign(new Error(message), {status: error.status, errors: error.error?.errors}));
  }

  private cleanParams(params?: Record<string, any>): Record<string, string | string[]> {
    if (!params) return {};

    return Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0))
      .reduce((acc, [key, value]) => {
        if (Array.isArray(value)) {
          acc[`${key}[]`] = value.map(String);
        } else {
          acc[key] = String(value);
        }
        return acc;
      }, {} as Record<string, string | string[]>);
  }
}
